import React, { useRef, useState, useMemo, useCallback } from "react";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
	ChartOptions,
} from "chart.js";
import type { TooltipModel } from "chart.js";
import { Bar } from "react-chartjs-2";
import styles from "./consumption-chart.module.scss";
import { DateRangePreset } from "../../../components/date-range/daterange-picker.types";
import {
	formatDateRange,
	getLastNDays,
	getThisMonth,
} from "../../../components/date-range/date-range.utils";
import { DateRangePicker, DateRangeButton } from "../../../components";
import ConsumptionChartSkeleton from "./consumption-chart-skeleton";
import { useGetSolarConsumptionGraphQuery } from "../../../../../service/vending-service";
import useWindowDimensions, { CurrentScreen } from "../../../../../hooks/use-window-dimensions";
import { DateRangeChevronIcon } from "../../../../../assets/icons";


ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface ConsumptionChartProps {
	subscriptionId: string;
	isReady: boolean;
}

export const ConsumptionChart: React.FC<ConsumptionChartProps> = ({
	subscriptionId,
	isReady,
}) => {
	const currentScreen: CurrentScreen = useWindowDimensions();
	const chartRef = useRef(null);

	const [isPickerOpen, setIsPickerOpen] = useState(false);
	const [selectedPeriod, setSelectedPeriod] = useState("Weekly");
	const [chartType, setChartType] = useState<"daily" | "monthly">("daily");
	const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>(
		getLastNDays(8)
	);

	const apiParams = useMemo(
		() => ({
			From: dateRange.from.toISOString(),
			To: dateRange.to.toISOString(),
			Type: chartType,
			PayAsYouGoSubscriptionId: subscriptionId,
		}),
		[dateRange, chartType, subscriptionId]
	);

	const {
		data: apiResponse,
		isLoading,
		isFetching,
		error,
		refetch,
	} = useGetSolarConsumptionGraphQuery(apiParams, { skip: !isReady });

	const presets: DateRangePreset[] = [
		{ label: "Weekly", getValue: () => getLastNDays(8) },
		{ label: "Monthly", getValue: getThisMonth },
	];

	const handleApply = (result: any) => {
		setDateRange({ from: result.from, to: result.to });
		if (result.type === "preset" && result.presetLabel) {
			setSelectedPeriod(result.presetLabel);
			setChartType(result.presetLabel === "Monthly" ? "monthly" : "daily");
		} else {
			setSelectedPeriod(formatDateRange(result.from, result.to));
			setChartType("daily");
		}
	};

	const processedData = useMemo(() => {
		if (
			!apiResponse?.solarConsumptionGraphItems ||
			apiResponse.solarConsumptionGraphItems.length === 0
		) {
			return { labels: [], values: [], totalConsumption: 0 };
		}

		const today = new Date();
		today.setHours(0, 0, 0, 0);

		const labels = apiResponse.solarConsumptionGraphItems.map((item) => {
			const date = new Date(item.date);
			if (chartType === "monthly") {
				return date.toLocaleDateString("en-US", { month: "short" });
			}
			return date.toLocaleDateString("en-US", { weekday: "short" });
		});

		const values = apiResponse.solarConsumptionGraphItems.map(
			(item) => item.consumed
		);

		return {
			labels,
			values,
			totalConsumption: apiResponse.totalConsumption,
		};
	}, [apiResponse, chartType]);

	const { labels, values, totalConsumption } = processedData;

	const maxValue = values.length > 0 ? Math.max(...values) : 150;

	const barColors = useMemo(() => {
		if (!apiResponse?.solarConsumptionGraphItems) return [];

		const today = new Date();
		today.setHours(0, 0, 0, 0);

		return apiResponse.solarConsumptionGraphItems.map((item) => {
			const itemDate = new Date(item.date);
			itemDate.setHours(0, 0, 0, 0);
			return itemDate.getTime() === today.getTime() ? "#F5E1D7" : "var(--app-landing-orange)";
		});
	}, [apiResponse]);

	const chartData = {
		labels,
		datasets: [
			{
				label: "Consumption",
				data: values,
				backgroundColor: barColors,
				borderRadius: 4,
				barPercentage: 0.7,
				categoryPercentage: 0.8,
			},
		],
	};

	const externalTooltipHandler = useCallback(
		(context: { chart: ChartJS; tooltip: TooltipModel<"bar"> }) => {
			const { chart, tooltip } = context;
			const chartContainer = chart.canvas.parentNode as HTMLElement;
			if (!chartContainer) return;

			let tooltipEl = chartContainer.querySelector(
				`.${styles.tooltipContainer}`
			) as HTMLElement | null;

			if (!tooltipEl) {
				tooltipEl = document.createElement("div");
				tooltipEl.className = styles.tooltipContainer;
				chartContainer.appendChild(tooltipEl);
			}

			if (tooltip.opacity === 0) {
				tooltipEl.style.opacity = "0";
				return;
			}

			const dataPoint = tooltip.dataPoints?.[0];
			const value = dataPoint?.parsed?.y ?? 0;
			const barElement = dataPoint?.element;
			const barTop = barElement?.y ?? tooltip.caretY;

			const canvasRect = chart.canvas.getBoundingClientRect();
			const containerRect = chartContainer.getBoundingClientRect();
			const offsetX = canvasRect.left - containerRect.left;
			const offsetY = canvasRect.top - containerRect.top;

			tooltipEl.innerHTML = `
				<div class="${styles.tooltip}">
					<span class="${styles.tooltipValue}">${value.toFixed(2)}</span>
					<span class="${styles.tooltipUnit}">kwh</span>
				</div>
				<div class="${styles.tooltipArrow}">
					<svg width="7" height="6" viewBox="0 0 7 6" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M4.34561 5.43792C3.95211 6.05132 3.05571 6.05132 2.66221 5.43792L0.161658 1.53995C-0.265297 0.874392 0.212622 -2.28827e-07 1.00336 -2.97955e-07L6.00446 -7.35166e-07C6.79519 -8.04294e-07 7.27311 0.87439 6.84616 1.53995L4.34561 5.43792Z" fill="white"/>
					</svg>
				</div>
			`;

			tooltipEl.style.opacity = "1";
			tooltipEl.style.left = `${tooltip.caretX + offsetX}px`;
			tooltipEl.style.top = `${barTop + offsetY - 58}px`;
		},
		[]
	);

	const options: ChartOptions<"bar"> = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: { display: false },
			tooltip: {
				enabled: false,
				external: externalTooltipHandler,
			},
		},
		scales: {
			x: {
				grid: { display: false },
				ticks: {
					color: "var(--app-base-white-05)",
					font: { size: currentScreen.isMobile ? 10 : 14 },
				},
				border: { display: false },
			},
			y: {
				position: "right",
				min: 0,
				max: Math.max(150, maxValue * 1.2),
				ticks: {
					color: "var(--app-base-white-07)",
					font: { size: currentScreen.isMobile ? 10 : 14 },
					stepSize: Math.max(50, Math.ceil(maxValue / 3)),
					callback: (value: number | string) => {
						const numValue =
							typeof value === "string" ? parseFloat(value) : value;
						return `${Math.floor(numValue)} kwh`;
					},
					padding: 10,
				},
				grid: { color: "var(--app-base-white-005)" },
				border: { display: false },
			},
		},
	};

	const renderError = () => (
		<div className={styles.errorState}>
			<svg width="48" height="48" viewBox="0 0 24 24" fill="none">
				<circle cx="12" cy="12" r="10" stroke="var(--app-landing-orange)" strokeWidth="2" />
				<path
					d="M12 8V12M12 16H12.01"
					stroke="var(--app-landing-orange)"
					strokeWidth="2"
					strokeLinecap="round"
				/>
			</svg>
			<p className={styles.errorText}>Failed to load consumption data</p>
			<button className={styles.retryButton} onClick={() => refetch()}>
				Retry
			</button>
		</div>
	);

	const renderEmptyState = () => (
		<div className={styles.emptyState}>
			<svg width="48" height="48" viewBox="0 0 24 24" fill="none">
				<path
					d="M9 11L12 14L22 4"
					stroke="var(--app-base-white-05)"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M21 12V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H16"
					stroke="var(--app-base-white-05)"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</svg>
			<p className={styles.emptyText}>
				No consumption data available for this period
			</p>
		</div>
	);

	return (
		<>
			<div className={styles.card}>
				<div className={styles.header}>
					<div className={styles.titleSection}>
						<h3 className={styles.title}>Total Consumption</h3>
						<div className={styles.totalValue}>
							<span className={styles.totalNumber}>
								{totalConsumption.toFixed(2)}
							</span>
							<span className={styles.totalUnit}>kwh</span>
						</div>
					</div>
					<DateRangeButton
						selectedLabel={selectedPeriod}
						onClick={() => setIsPickerOpen(true)}
						icon={<DateRangeChevronIcon />}
					/>
				</div>

				<div className={styles.chartContainer}>
					{isLoading || isFetching ? (
						<ConsumptionChartSkeleton />
					) : error ? (
						renderError()
					) : values.length === 0 ? (
						renderEmptyState()
					) : (
						<Bar ref={chartRef} data={chartData} options={options} />
					)}
				</div>
			</div>

			<DateRangePicker
				title="Date Filter"
				subtitle="Filter results by specific time periods"
				presets={presets}
				onApply={handleApply}
				onClose={() => setIsPickerOpen(false)}
				isOpen={isPickerOpen}
				initialFrom={dateRange.from}
				initialTo={dateRange.to}
				showCustomOption={true}
			/>
		</>
	);
};
